// ============================================
// SHIFT CLOSURE DIALOG COMPONENT
// Dialog de clôture de shift avec prévisualisation
// ============================================

import React, { useState, useEffect, useCallback } from 'react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    DialogDescription,
} from '../components/ui/dialog';
import {
    Alert,
    AlertDescription,
} from '../components/ui/alert';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
    Lock, 
    AlertTriangle, 
    CheckCircle2, 
    Clock, 
    UserX, 
    LogOut,
    Loader2,
    Info,
    ShieldCheck,
    History,
    Users
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

// Couleurs pour les statuts
const STATUS_COLORS = {
    'PRESENT': { bg: 'bg-green-100', text: 'text-green-700', badge: 'bg-green-500' },
    'LATE': { bg: 'bg-orange-100', text: 'text-orange-700', badge: 'bg-orange-500' },
    'ABSENT': { bg: 'bg-red-100', text: 'text-red-700', badge: 'bg-red-500' },
    'AUTO_ABSENT': { bg: 'bg-red-100', text: 'text-red-700', badge: 'bg-red-400' },
    'PENDING': { bg: 'bg-gray-100', text: 'text-gray-700', badge: 'bg-gray-400' },
    'NOT_CLOCKED': { bg: 'bg-yellow-100', text: 'text-yellow-700', badge: 'bg-yellow-500' }
};

const ShiftClosureDialog = ({ open, onOpenChange, date, onClosed }) => {
    const { api, hasPermission } = useAuth();
    const [loading, setLoading] = useState(false);
    const [closing, setClosing] = useState(false);
    const [preview, setPreview] = useState(null);
    const [closureStatus, setClosureStatus] = useState(null);
    
    // Options de clôture
    const [options, setOptions] = useState({
        autoMarkAbsent: true,
        autoClockOut: true,
        useShiftEndTime: true,
        notes: ''
    });
    
    const canClose = hasPermission('edit_shift');
    const canReopen = hasPermission('manage_admin_settings');

    // Charger la prévisualisation
    const fetchPreview = useCallback(async () => {
        if (!date || !open) return;
        
        try {
            setLoading(true);
            
            // Vérifier si déjà clôturé
            const statusRes = await api().get(`/shift-closure/status/${date}`);
            setClosureStatus(statusRes.data);
            
            // Charger la prévisualisation
            const res = await api().get(`/shift-closure/preview/${date}`);
            setPreview(res.data);
        } catch (error) {
            console.error('Error loading preview:', error);
            toast.error('Erreur lors du chargement de la prévisualisation');
        } finally {
            setLoading(false);
        }
    }, [api, date, open]);

    useEffect(() => {
        if (open && date) {
            fetchPreview();
        }
    }, [open, date, fetchPreview]);

    // Clôturer le shift
    const handleClose = async () => {
        if (!canClose || !preview) return;
        
        try {
            setClosing(true);
            
            const res = await api().post('/shift-closure/close', {
                date: date,
                auto_mark_absent: options.autoMarkAbsent,
                auto_clock_out: options.autoClockOut,
                use_shift_end_time: options.useShiftEndTime,
                notes: options.notes
            });
            
            toast.success(`Shift du ${date} clôturé avec succès`);
            
            if (res.data.auto_absent_count > 0) {
                toast.info(`${res.data.auto_absent_count} employé(s) marqué(s) absent(s) automatiquement`);
            }
            if (res.data.auto_clock_out_count > 0) {
                toast.info(`${res.data.auto_clock_out_count} sortie(s) automatique(s) ajoutée(s)`);
            }
            
            onClosed?.();
            onOpenChange(false);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de la clôture');
        } finally {
            setClosing(false);
        }
    };

    // Réouvrir le shift (Admin only)
    const handleReopen = async () => {
        if (!canReopen) return;
        
        if (!window.confirm(`Êtes-vous sûr de vouloir réouvrir le shift du ${date} ? Les pointages pourront être modifiés.`)) {
            return;
        }
        
        try {
            setClosing(true);
            await api().post(`/shift-closure/reopen/${date}`);
            toast.success(`Shift du ${date} réouvert`);
            fetchPreview();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de la réouverture');
        } finally {
            setClosing(false);
        }
    };

    const formatDate = (dateStr) => {
        const d = new Date(dateStr);
        return d.toLocaleDateString('fr-FR', { 
            weekday: 'long', 
            day: 'numeric', 
            month: 'long', 
            year: 'numeric' 
        });
    };

    const getStatusBadge = (status) => {
        const colors = STATUS_COLORS[status] || STATUS_COLORS['PENDING'];
        const labels = {
            'PRESENT': 'Présent',
            'LATE': 'Retard',
            'ABSENT': 'Absent',
            'AUTO_ABSENT': 'Absent (auto)',
            'PENDING': 'En attente',
            'NOT_CLOCKED': 'Non pointé'
        };
        
        return (
            <Badge className={`${colors.badge} text-white text-xs`}>
                {labels[status] || status}
            </Badge>
        );
    };

    const getActionBadge = (action) => {
        if (action === 'MARK_ABSENT') {
            return (
                <Badge variant="outline" className="text-red-600 border-red-300 text-xs">
                    <UserX className="w-3 h-3 mr-1" />
                    Sera marqué absent
                </Badge>
            );
        }
        if (action === 'AUTO_CLOCK_OUT') {
            return (
                <Badge variant="outline" className="text-blue-600 border-blue-300 text-xs">
                    <LogOut className="w-3 h-3 mr-1" />
                    Sortie auto
                </Badge>
            );
        }
        return null;
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-lg">
                        <Lock className="w-5 h-5 text-primary" />
                        Clôture du shift - {formatDate(date)}
                    </DialogTitle>
                    <DialogDescription>
                        Prévisualisation et clôture du shift. Cette action verrouillera les pointages.
                    </DialogDescription>
                </DialogHeader>
                
                {loading ? (
                    <div className="flex items-center justify-center py-12">
                        <Loader2 className="w-8 h-8 animate-spin text-primary" />
                    </div>
                ) : (
                    <div className="flex-1 overflow-hidden flex flex-col gap-4">
                        {/* Statut de clôture existant */}
                        {closureStatus?.is_closed && (
                            <Alert className="bg-green-50 border-green-200">
                                <ShieldCheck className="w-4 h-4 text-green-600" />
                                <AlertDescription className="text-green-700">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <strong>Shift déjà clôturé</strong>
                                            <p className="text-sm mt-1">
                                                Par {closureStatus.closure?.closed_by_name} le{' '}
                                                {new Date(closureStatus.closure?.closed_at).toLocaleString('fr-FR')}
                                            </p>
                                        </div>
                                        {canReopen && (
                                            <Button 
                                                variant="outline" 
                                                size="sm"
                                                onClick={handleReopen}
                                                disabled={closing}
                                                className="border-green-300 text-green-700 hover:bg-green-100"
                                            >
                                                <History className="w-4 h-4 mr-1" />
                                                Réouvrir
                                            </Button>
                                        )}
                                    </div>
                                </AlertDescription>
                            </Alert>
                        )}
                        
                        {/* Statistiques */}
                        {preview && (
                            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                                <div className="p-3 rounded-lg bg-blue-50 border border-blue-200 text-center">
                                    <Users className="w-5 h-5 mx-auto text-blue-600 mb-1" />
                                    <div className="text-2xl font-bold text-blue-700">{preview.total_scheduled}</div>
                                    <div className="text-xs text-blue-600">Planifiés</div>
                                </div>
                                <div className="p-3 rounded-lg bg-green-50 border border-green-200 text-center">
                                    <CheckCircle2 className="w-5 h-5 mx-auto text-green-600 mb-1" />
                                    <div className="text-2xl font-bold text-green-700">{preview.present_count}</div>
                                    <div className="text-xs text-green-600">Présents</div>
                                </div>
                                <div className="p-3 rounded-lg bg-orange-50 border border-orange-200 text-center">
                                    <Clock className="w-5 h-5 mx-auto text-orange-600 mb-1" />
                                    <div className="text-2xl font-bold text-orange-700">{preview.late_count}</div>
                                    <div className="text-xs text-orange-600">Retards</div>
                                </div>
                                <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-center">
                                    <UserX className="w-5 h-5 mx-auto text-red-600 mb-1" />
                                    <div className="text-2xl font-bold text-red-700">{preview.absent_count + preview.pending_count}</div>
                                    <div className="text-xs text-red-600">Absents</div>
                                </div>
                                <div className="p-3 rounded-lg bg-purple-50 border border-purple-200 text-center">
                                    <LogOut className="w-5 h-5 mx-auto text-purple-600 mb-1" />
                                    <div className="text-2xl font-bold text-purple-700">{preview.missing_clock_out_count}</div>
                                    <div className="text-xs text-purple-600">Sans sortie</div>
                                </div>
                            </div>
                        )}
                        
                        {/* Warnings */}
                        {preview?.warnings?.length > 0 && (
                            <Alert className="bg-amber-50 border-amber-200">
                                <AlertTriangle className="w-4 h-4 text-amber-600" />
                                <AlertDescription className="text-amber-700">
                                    <ul className="list-disc list-inside text-sm space-y-1">
                                        {preview.warnings.map((w, i) => (
                                            <li key={i}>{w}</li>
                                        ))}
                                    </ul>
                                </AlertDescription>
                            </Alert>
                        )}
                        
                        {/* Liste des employés */}
                        {preview?.employees_details?.length > 0 && (
                            <div className="flex-1 overflow-hidden">
                                <div className="text-sm font-medium text-muted-foreground mb-2 flex items-center gap-2">
                                    <Info className="w-4 h-4" />
                                    Détail par employé
                                </div>
                                <ScrollArea className="h-[200px] border rounded-lg">
                                    <div className="p-2 space-y-2">
                                        {preview.employees_details.map((emp) => (
                                            <div 
                                                key={emp.schedule_id}
                                                className={`p-3 rounded-lg border ${emp.is_closed ? 'bg-gray-50 opacity-70' : 'bg-white'}`}
                                            >
                                                <div className="flex items-center justify-between">
                                                    <div>
                                                        <div className="font-medium">{emp.staff_name}</div>
                                                        <div className="text-xs text-muted-foreground">
                                                            {emp.service} • {emp.start_time} - {emp.end_time}
                                                        </div>
                                                        {emp.check_in_time && (
                                                            <div className="text-xs text-green-600 mt-1">
                                                                Entrée: {new Date(emp.check_in_time).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                                                                {emp.check_out_time && ` • Sortie: ${new Date(emp.check_out_time).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`}
                                                            </div>
                                                        )}
                                                        {emp.late_minutes > 0 && (
                                                            <div className="text-xs text-orange-600">
                                                                Retard: {emp.late_minutes} min
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className="flex flex-col items-end gap-1">
                                                        {getStatusBadge(emp.status)}
                                                        {!closureStatus?.is_closed && getActionBadge(emp.action_needed)}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </ScrollArea>
                            </div>
                        )}
                        
                        {/* Options de clôture */}
                        {!closureStatus?.is_closed && preview?.can_close && (
                            <div className="space-y-3 p-4 border rounded-lg bg-muted/30">
                                <div className="text-sm font-medium">Options de clôture</div>
                                
                                <div className="flex items-center space-x-2">
                                    <Checkbox 
                                        id="autoAbsent"
                                        checked={options.autoMarkAbsent}
                                        onCheckedChange={(v) => setOptions(prev => ({ ...prev, autoMarkAbsent: v }))}
                                    />
                                    <Label htmlFor="autoAbsent" className="text-sm cursor-pointer">
                                        Marquer automatiquement les non-pointés comme <span className="text-red-600 font-medium">Absent</span>
                                    </Label>
                                </div>
                                
                                <div className="flex items-center space-x-2">
                                    <Checkbox 
                                        id="autoClockOut"
                                        checked={options.autoClockOut}
                                        onCheckedChange={(v) => setOptions(prev => ({ ...prev, autoClockOut: v }))}
                                    />
                                    <Label htmlFor="autoClockOut" className="text-sm cursor-pointer">
                                        Ajouter une <span className="text-blue-600 font-medium">sortie automatique</span> aux pointages incomplets
                                    </Label>
                                </div>
                                
                                {options.autoClockOut && (
                                    <div className="flex items-center space-x-2 ml-6">
                                        <Checkbox 
                                            id="useShiftEnd"
                                            checked={options.useShiftEndTime}
                                            onCheckedChange={(v) => setOptions(prev => ({ ...prev, useShiftEndTime: v }))}
                                        />
                                        <Label htmlFor="useShiftEnd" className="text-sm cursor-pointer">
                                            Utiliser l'heure de fin de shift (sinon heure actuelle)
                                        </Label>
                                    </div>
                                )}
                                
                                <div>
                                    <Label htmlFor="notes" className="text-sm">Notes (optionnel)</Label>
                                    <Textarea
                                        id="notes"
                                        placeholder="Remarques sur cette clôture..."
                                        value={options.notes}
                                        onChange={(e) => setOptions(prev => ({ ...prev, notes: e.target.value }))}
                                        className="mt-1 h-20"
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                )}
                
                <DialogFooter className="gap-2">
                    <Button variant="outline" onClick={() => onOpenChange(false)}>
                        {closureStatus?.is_closed ? 'Fermer' : 'Annuler'}
                    </Button>
                    
                    {!closureStatus?.is_closed && preview?.can_close && canClose && (
                        <Button 
                            onClick={handleClose}
                            disabled={closing || !preview?.can_close}
                            className="bg-primary"
                        >
                            {closing ? (
                                <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    Clôture en cours...
                                </>
                            ) : (
                                <>
                                    <Lock className="w-4 h-4 mr-2" />
                                    Confirmer la clôture
                                </>
                            )}
                        </Button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default ShiftClosureDialog;
